/**
 * Beranda (Lima Angka) aggregates, dialect-neutral (spec 13.3, DISCUSSION-kerangka-beranda):
 * the four statements that feed the page and the pure computation over their rows. The
 * device worker runs them over SQLite during the launch splash; the API can run the same
 * statements over Postgres for the diocese view. Everything device-only (the outbox count,
 * the activity log, the sync_state cache) lives in apps/web/src/db/aggregates.ts.
 *
 * Scoping is done here, in TypeScript, so the statements take no parameters: a parish
 * device holds one parish, a ketua lingkungan device one lingkungan, a ketua stasi device
 * the stasi's lingkungan, a diocese device every parish; the caller says which grouping it
 * wants (per lingkungan or per parish) and, when known, which parish or lingkungan the
 * session belongs to.
 */
import { kelengkapanUmat, type UmatKelengkapan } from '../completeness'
import { usiaPada } from '../dates/partial'
import type { TABEL_REGISTER } from '../schema/contract'
import ui from '../strings/ui.id.json' with { type: 'json' }
import { isiUnit, SEBUTAN_BAWAAN, type SebutanUnit } from '../strings/unit'

export type KelompokBeranda = 'lingkungan' | 'paroki'
export type RegisterBeranda = (typeof TABEL_REGISTER)[number]

// ---------------------------------------------------------------------------
// Rows as the statements return them
// ---------------------------------------------------------------------------

export interface UnitBerandaRow {
  readonly id: string
  readonly paroki_id: string | null
  readonly jenis: string
  readonly induk_id: string | null
  readonly nama: string
  readonly urutan: number
}

export interface UmatBerandaRow extends UmatKelengkapan {
  readonly paroki_id: string | null
  readonly lingkungan_id: string | null
  readonly keluarga_id: string | null
  readonly diubah_pada: string
}

export interface KeluargaBerandaRow {
  readonly id: string
  readonly paroki_id: string | null
  readonly lingkungan_id: string | null
  readonly diubah_pada: string
}

export interface SakramenBerandaRow {
  readonly register: RegisterBeranda
  readonly paroki_id: string | null
  /** The first four characters of the date, or null when the entry has no date. */
  readonly tahun: string | null
  readonly n: number
}

export interface BerandaSumber {
  readonly unit: readonly UnitBerandaRow[]
  readonly umat: readonly UmatBerandaRow[]
  readonly keluarga: readonly KeluargaBerandaRow[]
  readonly sakramen: readonly SakramenBerandaRow[]
}

/** Kematian counts by the date of death (spec 13.1), falling back to the entry date. */
export function kolomTanggalRegister(register: RegisterBeranda): string {
  return register === 'kematian' ? 'coalesce(tanggal_meninggal, tanggal)' : 'tanggal'
}

/** Parameter-free statements that run unchanged on SQLite and Postgres. */
export const BERANDA_SQL = {
  unit: "SELECT id, paroki_id, jenis, induk_id, nama, urutan FROM unit_teritorial WHERE dihapus_pada IS NULL AND jenis IN ('paroki', 'lingkungan') ORDER BY urutan, nama",
  umat: "SELECT id, paroki_id, lingkungan_id, keluarga_id, nama_lengkap, jenis_kelamin, tanggal_lahir, nik, status_gereja, pendidikan_kode, pekerjaan_kode, status_perkawinan, baptis_id, referensi_sakramen_luar, diubah_pada FROM umat WHERE dihapus_pada IS NULL AND status_hidup = 'hidup' AND pindah_keuskupan IS NULL",
  keluarga:
    "SELECT id, paroki_id, lingkungan_id, diubah_pada FROM keluarga WHERE dihapus_pada IS NULL AND status = 'aktif'",
  sakramen(register: RegisterBeranda): string {
    const kolom = kolomTanggalRegister(register)
    return `SELECT paroki_id, substr(${kolom}, 1, 4) AS tahun, count(*) AS n FROM ${register} WHERE dihapus_pada IS NULL AND ${kolom} IS NOT NULL GROUP BY paroki_id, substr(${kolom}, 1, 4)`
  },
} as const

// ---------------------------------------------------------------------------
// Result
// ---------------------------------------------------------------------------

export interface AngkaBeranda {
  readonly umat: number
  readonly keluarga: number
  /** null on a ketua device (lingkungan or stasi): the registers are not there (spec 13.1). */
  readonly baptisTahunIni: number | null
  readonly perkawinanTahunIni: number | null
}

export interface KelengkapanBaris {
  readonly id: string
  readonly nama: string
  readonly keluarga: number
  readonly umat: number
  /** Share of umat whose age-banded required columns are filled; null without umat. */
  readonly lengkapPct: number | null
  /** ISO timestamp of the last change to a family or person of the group; null when empty. */
  readonly terakhirDiubah: string | null
}

export const PITA_USIA = ['0-6', '7-14', '15-24', '25-59', '60+'] as const
export type PitaUsiaBeranda = (typeof PITA_USIA)[number]

export interface PiramidaBaris {
  readonly pita: PitaUsiaBeranda
  readonly L: number
  readonly P: number
}

export interface SakramenTahunBaris {
  readonly tahun: number
  readonly baptis: number
  readonly komuni_pertama: number
  readonly krisma: number
  readonly perkawinan: number
  readonly kematian: number
}

export interface UmatPerKelompokBaris {
  readonly id: string
  readonly nama: string
  readonly umat: number
}

export interface GrafikBeranda {
  readonly piramidaUsia: readonly PiramidaBaris[]
  /** Living umat without a birth date: they are in the figures but not in the pyramid. */
  readonly tanpaTanggalLahir: number
  /** Empty on a ketua device (lingkungan or stasi). */
  readonly sakramenPerTahun: readonly SakramenTahunBaris[]
  readonly umatPerKelompok: readonly UmatPerKelompokBaris[]
}

export interface BerandaHasil {
  readonly tahun: number
  readonly kelompok: KelompokBeranda
  readonly angka: AngkaBeranda
  readonly kelengkapan: readonly KelengkapanBaris[]
  readonly grafik: GrafikBeranda
}

export interface BerandaOpsi {
  /** "Tahun ini" for the two sacrament figures and the red chart series. */
  readonly tahun: number
  /** Today, for ages and the completeness bands. */
  readonly pada: string | Date
  readonly kelompok: KelompokBeranda
  /** The session's parish; rows of other parishes are ignored when set. */
  readonly parokiId?: string | null | undefined
  /** A ketua lingkungan's lingkungan: one group, no sacrament figures, no sacrament chart. */
  readonly lingkunganId?: string | null | undefined
  /**
   * A ketua stasi's lingkungan: these groups only, nothing outside them, no sacrament
   * figures (spec 2026-09-30, 7). An empty list counts nothing.
   */
  readonly lingkunganIds?: readonly string[] | null | undefined
  /** The parish's word for its smallest unit ("Tanpa kombas"); the default when absent. */
  readonly sebutan?: SebutanUnit | undefined
}

/** How many years the sacrament chart shows, the current year included. */
export const TAHUN_GRAFIK_SAKRAMEN = 3

const TANPA_KELOMPOK_ID = '__tanpa_kelompok'

function pitaDariUsia(usia: number): PitaUsiaBeranda {
  if (usia <= 6) return '0-6'
  if (usia <= 14) return '7-14'
  if (usia <= 24) return '15-24'
  if (usia <= 59) return '25-59'
  return '60+'
}

function maxIso(a: string | null, b: string | null): string | null {
  if (a === null) return b
  if (b === null) return a
  return a > b ? a : b
}

/**
 * Resolve a lingkungan a person types or a session carries to a unit id: an id, the bare
 * name ("St. Maria") or the labelled name ("Lingkungan St. Maria"). Null when unknown.
 */
export function cariLingkungan(
  unit: readonly UnitBerandaRow[],
  petunjuk: string | null | undefined,
): string | null {
  if (petunjuk === null || petunjuk === undefined || petunjuk.trim() === '') return null
  const teks = petunjuk.trim()
  const tanpaLabel = teks.replace(/^lingkungan\s+/i, '')
  const cocok = unit.find(
    (u) =>
      u.jenis === 'lingkungan' &&
      (u.id === teks ||
        u.nama === teks ||
        u.nama === tanpaLabel ||
        `Lingkungan ${u.nama}` === teks),
  )
  return cocok?.id ?? null
}

interface Kelompok {
  id: string
  nama: string
  keluarga: number
  umat: number
  lengkap: number
  terakhir: string | null
}

export function hitungBeranda(sumber: BerandaSumber, opsi: BerandaOpsi): BerandaHasil {
  const parokiId = opsi.parokiId ?? null
  const lingkunganId = opsi.lingkunganId ?? null
  const scopedLingkungan = opsi.kelompok === 'lingkungan' && lingkunganId !== null
  // A ketua stasi's device: its groups only; like a ketua lingkungan's, it holds no book.
  const daftar = opsi.kelompok === 'lingkungan' ? (opsi.lingkunganIds ?? null) : null
  const tanpaSisa = scopedLingkungan || daftar !== null

  // The groups, in the parish's own order, before any row is read: an empty lingkungan
  // is a row of zeros, which is what the diocese wants to see.
  const kelompokList: Kelompok[] = []
  const kelompokById = new Map<string, Kelompok>()
  for (const unit of sumber.unit) {
    if (unit.jenis !== opsi.kelompok) continue
    if (opsi.kelompok === 'lingkungan') {
      if (parokiId !== null && unit.paroki_id !== null && unit.paroki_id !== parokiId) continue
      if (lingkunganId !== null && unit.id !== lingkunganId) continue
      if (daftar !== null && !daftar.includes(unit.id)) continue
    }
    const k: Kelompok = {
      id: unit.id,
      nama: unit.nama,
      keluarga: 0,
      umat: 0,
      lengkap: 0,
      terakhir: null,
    }
    kelompokList.push(k)
    kelompokById.set(unit.id, k)
  }
  const kelompokDari = (key: string | null): Kelompok | null => {
    if (key !== null) {
      const found = kelompokById.get(key)
      if (found !== undefined) return found
    }
    if (tanpaSisa) return null
    let tanpa = kelompokById.get(TANPA_KELOMPOK_ID)
    if (tanpa === undefined) {
      tanpa = {
        id: TANPA_KELOMPOK_ID,
        nama:
          opsi.kelompok === 'lingkungan'
            ? isiUnit(ui.laporan.tanpa.lingkungan, opsi.sebutan ?? SEBUTAN_BAWAAN)
            : ui.laporan.tanpa.paroki,
        keluarga: 0,
        umat: 0,
        lengkap: 0,
        terakhir: null,
      }
      kelompokById.set(TANPA_KELOMPOK_ID, tanpa)
      kelompokList.push(tanpa)
    }
    return tanpa
  }

  const dalamParoki = (id: string | null): boolean =>
    parokiId === null || (opsi.kelompok === 'paroki' ? true : id === null || id === parokiId)

  // Families: the lingkungan of a person without one of their own comes from the family.
  const lingkunganKeluarga = new Map<string, string | null>()
  let keluargaTotal = 0
  for (const row of sumber.keluarga) {
    if (!dalamParoki(row.paroki_id)) continue
    lingkunganKeluarga.set(row.id, row.lingkungan_id)
    const key = opsi.kelompok === 'lingkungan' ? row.lingkungan_id : row.paroki_id
    if (scopedLingkungan && key !== lingkunganId) continue
    const k = kelompokDari(key)
    if (k === null) continue
    k.keluarga += 1
    k.terakhir = maxIso(k.terakhir, row.diubah_pada)
    keluargaTotal += 1
  }

  // People: figures, completeness, the pyramid, umat per group.
  const piramida = new Map<PitaUsiaBeranda, { L: number; P: number }>(
    PITA_USIA.map((pita) => [pita, { L: 0, P: 0 }]),
  )
  let umatTotal = 0
  let tanpaTanggalLahir = 0
  for (const row of sumber.umat) {
    if (!dalamParoki(row.paroki_id)) continue
    const lingkungan =
      row.lingkungan_id ??
      (row.keluarga_id === null ? null : (lingkunganKeluarga.get(row.keluarga_id) ?? null))
    const key = opsi.kelompok === 'lingkungan' ? lingkungan : row.paroki_id
    if (scopedLingkungan && key !== lingkunganId) continue
    const k = kelompokDari(key)
    if (k === null) continue
    umatTotal += 1
    k.umat += 1
    k.terakhir = maxIso(k.terakhir, row.diubah_pada)
    const hasil = kelengkapanUmat(row, opsi.pada)
    if (hasil.lengkap) k.lengkap += 1
    if (hasil.usia === null) {
      tanpaTanggalLahir += 1
    } else if (row.jenis_kelamin === 'L' || row.jenis_kelamin === 'P') {
      const band = piramida.get(pitaDariUsia(Math.max(0, hasil.usia)))
      if (band !== undefined) band[row.jenis_kelamin] += 1
    }
  }

  // Sacraments: the two figures and the last three years, unless this device has no books.
  const perTahun = new Map<number, Record<RegisterBeranda, number>>()
  const tahunList: number[] = []
  for (let i = TAHUN_GRAFIK_SAKRAMEN - 1; i >= 0; i--) {
    const tahun = opsi.tahun - i
    tahunList.push(tahun)
    perTahun.set(tahun, { baptis: 0, komuni_pertama: 0, krisma: 0, perkawinan: 0, kematian: 0 })
  }
  if (!tanpaSisa) {
    for (const row of sumber.sakramen) {
      if (row.tahun === null) continue
      if (parokiId !== null && opsi.kelompok === 'lingkungan' && row.paroki_id !== parokiId)
        continue
      const tahun = Number(row.tahun)
      const bucket = perTahun.get(tahun)
      if (bucket !== undefined) bucket[row.register] += Number(row.n)
    }
  }
  const tahunIni = perTahun.get(opsi.tahun)

  const kelengkapan: KelengkapanBaris[] = kelompokList.map((k) => ({
    id: k.id,
    nama: k.nama,
    keluarga: k.keluarga,
    umat: k.umat,
    lengkapPct: k.umat === 0 ? null : Math.round((100 * k.lengkap) / k.umat),
    terakhirDiubah: k.terakhir,
  }))

  return {
    tahun: opsi.tahun,
    kelompok: opsi.kelompok,
    angka: {
      umat: umatTotal,
      keluarga: keluargaTotal,
      baptisTahunIni: tanpaSisa ? null : (tahunIni?.baptis ?? 0),
      perkawinanTahunIni: tanpaSisa ? null : (tahunIni?.perkawinan ?? 0),
    },
    kelengkapan,
    grafik: {
      piramidaUsia: PITA_USIA.map((pita) => {
        const band = piramida.get(pita) ?? { L: 0, P: 0 }
        return { pita, L: band.L, P: band.P }
      }),
      tanpaTanggalLahir,
      sakramenPerTahun: tanpaSisa
        ? []
        : tahunList.map((tahun) => ({
            tahun,
            ...(perTahun.get(tahun) ?? {
              baptis: 0,
              komuni_pertama: 0,
              krisma: 0,
              perkawinan: 0,
              kematian: 0,
            }),
          })),
      umatPerKelompok: kelompokList
        .filter((k) => k.umat > 0 || k.id !== TANPA_KELOMPOK_ID)
        .map((k) => ({ id: k.id, nama: k.nama, umat: k.umat }))
        .sort((a, b) => b.umat - a.umat || a.nama.localeCompare(b.nama, 'id')),
    },
  }
}

/** Age helper shared with tests: the band a completed age falls in. */
export function pitaUsiaBeranda(usia: number): PitaUsiaBeranda {
  return pitaDariUsia(usia)
}

/** Completed years today for a partial birth date, or null when unknown. */
export function usiaBeranda(tanggalLahir: string | null, pada: string | Date): number | null {
  if (tanggalLahir === null || tanggalLahir.trim() === '') return null
  return usiaPada(tanggalLahir, pada)?.tahun ?? null
}
